import type { ReactNode } from 'react'
import { useCallback, useState } from 'react'
import { Pressable, Text, TextInput, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Redirect, useRouter } from 'expo-router'
import Head from 'expo-router/head'
import { StatusBar } from 'expo-status-bar'
import { RetrySignOutErrorWall } from '@/src/components/ui/RetrySignOutErrorWall'
import { useAppSession } from '@/src/contexts/AppSessionContext'
import { useResolvedAuthState } from '@/src/hooks/useResolvedAuthState'
import { pendingSignupFirstNameQueryOptions } from '@/src/queries/auth-flow-queries'
import { extractApiErrorMessage } from '@/src/utils/parseApiError'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ScreenLoadingFallback } from '../../../components/ui/ScreenLoadingFallback'
import { Spinner } from '../../../components/ui/Spinner'
import {
  buildSignupProperties,
  identifyUser,
  logBranchEvent,
  MixpanelEvents,
  trackEvent,
} from '../../../services/analytics'
import { colors } from '../../../theme'
import {
  useCreateUserMutation,
  useCreateUserStatus,
} from '../hooks/useCreateUserMutation'
import { resolveCreateUserFirstName } from '../utils/create-user-name'

type NameState =
  | { status: 'checking' }
  | { status: 'missing' }
  | { status: 'resolved'; firstName: string }

export default function CreateUser() {
  const {
    user,
    isAuthLoading,
    error: authError,
    authorize,
  } = useResolvedAuthState()
  const {
    backendUser,
    backendUserError,
    logout,
    backendUserStatus,
    refreshUserStatus,
  } = useAppSession()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [nameInput, setNameInput] = useState('')
  const { t } = useTranslation(['common', 'profile', 'setup'])
  const email = user?.email ?? null
  const hasAuthUser = user != null

  const handleGoBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back()
      return
    }

    router.replace('/onboarding')
  }, [router])

  const createUserMutation = useCreateUserMutation()
  const createUserStatus = useCreateUserStatus()
  const {
    data: pendingSignupFirstName,
    isPending: isPendingSignupFirstNamePending,
  } = useSuspenseQuery(pendingSignupFirstNameQueryOptions())

  const resolvedFirstName = resolveCreateUserFirstName({
    pendingSignupFirstName: pendingSignupFirstName ?? null,
    authUser: user,
  })
  const nameState: NameState = isPendingSignupFirstNamePending
    ? { status: 'checking' }
    : resolvedFirstName != null
      ? { status: 'resolved', firstName: resolvedFirstName }
      : { status: 'missing' }
  const resolvedNameForAutoCreate =
    nameState.status === 'resolved' ? nameState.firstName : null

  const handleCreateUserSuccess = useCallback(
    async (resolvedMixpanelId: string | null | undefined) => {
      if (resolvedMixpanelId != null && resolvedMixpanelId.trim().length > 0) {
        await identifyUser(resolvedMixpanelId, user?.name, user?.email)
      }

      if (
        user?.sub &&
        (!resolvedMixpanelId || resolvedMixpanelId.trim().length === 0)
      ) {
        console.warn(
          'Created user is missing backend Mixpanel ID; signup identify skipped.',
        )
      }

      trackEvent(
        MixpanelEvents.SignupCompleted,
        buildSignupProperties({ email }),
      )
      logBranchEvent('COMPLETE_REGISTRATION')
    },
    [email, user?.email, user?.name, user?.sub],
  )

  if (isAuthLoading || backendUserStatus === 'loading') {
    return (
      <ScreenLoadingFallback
        surface="transparent"
        spinnerColor={colors.accent}
      />
    )
  }

  if (authError) {
    return (
      <RetrySignOutErrorWall
        title={t('setup:error_title')}
        message={authError.message}
        onRetry={authorize}
        secondaryActionLabel={
          hasAuthUser ? undefined : t('common:error_go_back')
        }
        onSignOut={hasAuthUser ? logout : handleGoBack}
      />
    )
  }

  if (backendUserStatus === 'error') {
    return (
      <RetrySignOutErrorWall
        title={t('setup:error_title')}
        message={
          extractApiErrorMessage(backendUserError) ?? t('setup:error_generic')
        }
        onRetry={refreshUserStatus}
        secondaryActionLabel={
          hasAuthUser ? undefined : t('common:error_go_back')
        }
        onSignOut={hasAuthUser ? logout : handleGoBack}
      />
    )
  }

  if (user == null) {
    return <Redirect href="/onboarding" />
  }

  if (backendUserStatus === 'ready') {
    return <Redirect href="/auth-complete" />
  }

  if (
    backendUserStatus === 'missing' &&
    resolvedNameForAutoCreate != null &&
    createUserStatus !== 'error'
  ) {
    return <Redirect href="/auth-complete" />
  }

  const handleNameSubmit = () => {
    const trimmed = nameInput.trim()
    if (trimmed.length === 0) return

    if (createUserMutation.isError) {
      createUserMutation.reset()
    }
    queryClient.setQueryData(
      pendingSignupFirstNameQueryOptions().queryKey,
      trimmed,
    )
    createUserMutation.mutate(
      { email, firstName: trimmed },
      {
        onSuccess: (resolvedBackendUser) => {
          void handleCreateUserSuccess(
            resolvedBackendUser?.mixpanelId ?? backendUser?.mixpanelId,
          )
        },
      },
    )
  }

  const handleRetry = () => {
    createUserMutation.reset()

    if (resolvedNameForAutoCreate != null) {
      router.replace('/auth-complete')
    }
  }

  const handleLogout = async () => {
    if (isLoggingOut) return
    setIsLoggingOut(true)
    try {
      await logout()
      router.replace('/onboarding')
    } catch (e) {
      console.error('Logout failed', e)
      setIsLoggingOut(false)
    }
  }

  const errorMessage =
    extractApiErrorMessage(createUserMutation.error) ??
    t('setup:create_user_failed')

  let content: ReactNode

  if (nameState.status === 'checking') {
    content = (
      <ScreenLoadingFallback
        surface="transparent"
        spinnerColor={colors.accent}
      />
    )
  } else if (nameState.status === 'missing') {
    content = (
      <View className="flex-1 justify-center items-center px-6">
        <Text className="text-2xl font-bold text-black mb-3 text-center">
          {t('setup:missing_name_title')}
        </Text>
        <Text className="text-base text-gray-600 text-center mb-8">
          {t('setup:missing_name_subtitle')}
        </Text>
        <Text className="self-stretch text-sm font-semibold text-black mb-2">
          {t('setup:first_name_label')}
        </Text>
        <TextInput
          className="self-stretch h-12 rounded-lg border border-border bg-background px-4 py-0 text-base text-ink mb-6"
          style={{
            lineHeight: 20,
            includeFontPadding: false,
          }}
          placeholder={t('setup:first_name_placeholder')}
          placeholderTextColor={colors.muted}
          value={nameInput}
          onChangeText={setNameInput}
          autoCapitalize="words"
          autoCorrect={false}
          textAlignVertical="center"
        />
        <Pressable
          className={`h-[50px] self-stretch rounded-[25px] px-8 items-center justify-center ${nameInput.trim().length === 0 ? 'bg-gray-300' : 'bg-accent'}`}
          onPress={handleNameSubmit}
          disabled={nameInput.trim().length === 0}
        >
          <Text className="text-base font-bold text-accent-foreground">
            {t('setup:missing_name_continue')}
          </Text>
        </Pressable>
      </View>
    )
  } else if (createUserStatus === 'error' || createUserMutation.isError) {
    content = (
      <View className="flex-1 justify-center items-center px-6">
        <Text className="text-display-emoji mb-4">⚠️</Text>
        <Text className="text-2xl font-bold text-black mb-3 text-center">
          {t('setup:error_title')}
        </Text>
        <Text className="text-base text-gray-600 text-center mb-8">
          {errorMessage}
        </Text>
        <Pressable
          className="h-[50px] bg-accent rounded-[25px] px-8 items-center justify-center shadow-lg"
          onPress={handleRetry}
        >
          <Text className="text-base font-bold text-accent-foreground">
            {t('setup:try_again')}
          </Text>
        </Pressable>
        <Pressable
          className="h-[50px] bg-transparent rounded-[25px] border px-8 items-center justify-center mt-3"
          style={{
            borderColor: isLoggingOut ? colors.grayMid : colors.accent,
          }}
          onPress={handleLogout}
          disabled={isLoggingOut}
        >
          {isLoggingOut ? (
            <Spinner size="small" color={colors.accent} />
          ) : (
            <Text className="text-base font-semibold text-accent">
              {t('profile:logout')}
            </Text>
          )}
        </Pressable>
      </View>
    )
  } else {
    content = (
      <ScreenLoadingFallback surface="transparent" spinnerColor={colors.accent}>
        <Text className="text-xl font-semibold text-black mt-6 text-center">
          {t('setup:creating_account')}
        </Text>
        <Text className="text-base text-gray-600 mt-2 text-center">
          {t('setup:creating_subtitle')}
        </Text>
      </ScreenLoadingFallback>
    )
  }

  return (
    <SafeAreaView
      className="flex-1 bg-background-primary"
      edges={['top', 'left', 'right', 'bottom']}
    >
      <Head>
        <title>{t('common:page_title_create_account')}</title>
      </Head>
      <StatusBar style="dark" />
      {content}
    </SafeAreaView>
  )
}
